import { useMemo, useState } from 'react';

// Components
import Product from 'components/Product';

import { Trans } from '@lingui/macro';

export default function ProductList(props) {
    const {
        products,
        parentMode,
        bundles,
        groups,
        chargesEnabled,
        onAddProduct,
        onRemoveProduct,
        onAddBundle,
        onRemoveBundle,
        currency,
    } = props;

    const [selectedGroup, setSelectedGroup] = useState(null);

    const productGroups = useMemo(() => {
        if (!Array.isArray(groups)) return [];

        const availableGroupIds = new Set(
            [...products, ...bundles]
                .map((item) => item.prodGroupId)
                .filter((groupId) => typeof groupId === 'number')
                .map((groupId) => groupId),
        );

        return groups.filter((group) => availableGroupIds.has(group.prod_group_id));
    }, [groups, products, bundles]);

    const filteredBundles = useMemo(
        () => filterByGroup(bundles, selectedGroup),
        [bundles, selectedGroup],
    );

    const filteredProducts = useMemo(
        () => filterByGroup(products, selectedGroup),
        [products, selectedGroup],
    );

    const tickets = filteredProducts.filter((p) => p.type === 1);
    const addons = filteredProducts.filter((p) => p.type === 2);

    return (
        <div className="max-w-screen-xl mx-auto p-4 lg:pt-8">
            {productGroups.length > 0 && (
                <GroupFilter
                    groups={productGroups}
                    selectedGroup={selectedGroup}
                    onGroupChange={setSelectedGroup}
                />
            )}

            {/* Bundles Section */}
            {filteredBundles.length > 0 && (
                <section className="mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                        <Trans>Bundles</Trans>
                    </h2>
                    {filteredBundles.map((b) => (
                        <Product
                            key={b.id}
                            product={b}
                            chargesEnabled={chargesEnabled}
                            currency={currency}
                            onAdd={onAddBundle}
                            onRemove={onRemoveBundle}
                        />
                    ))}
                </section>
            )}

            {/* Tickets Section */}
            {tickets.length > 0 && (
                <section className="mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                        {parentMode ? <Trans>Season Tickets</Trans> : <Trans>Tickets</Trans>}
                    </h2>
                    {tickets.map((p) => (
                        <Product
                            key={p.id}
                            product={p}
                            chargesEnabled={chargesEnabled}
                            currency={currency}
                            onAdd={onAddProduct}
                            onRemove={onRemoveProduct}
                        />
                    ))}
                </section>
            )}

            {/* Add-ons Section */}
            {addons.length > 0 && (
                <section className="mb-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                        <Trans>Add-ons</Trans>
                    </h2>
                    {addons.map((p) => (
                        <Product
                            key={p.id}
                            product={p}
                            chargesEnabled={chargesEnabled}
                            currency={currency}
                            onAdd={onAddProduct}
                            onRemove={onRemoveProduct}
                        />
                    ))}
                </section>
            )}
        </div>
    );
}

function filterByGroup(items, selectedGroup) {
    if (selectedGroup === null || selectedGroup === undefined) return items;

    return items.filter((item) => item.prodGroupId === selectedGroup);
}

function GroupFilter({ groups, selectedGroup, onGroupChange }) {
    const isAllSelected = selectedGroup === null || selectedGroup === undefined;

    return (
        <div className="mb-8 flex flex-wrap justify-center gap-2">
            <FilterChip selected={isAllSelected} onClick={() => onGroupChange(null)}>
                <Trans>All Items</Trans>
            </FilterChip>

            {groups.map((group) => {
                const isSelected = selectedGroup === group.prod_group_id;

                return (
                    <FilterChip
                        key={group.prod_group_id}
                        selected={isSelected}
                        onClick={() => onGroupChange(isSelected ? null : group.prod_group_id)}
                    >
                        {group.prod_group_name}
                    </FilterChip>
                );
            })}
        </div>
    );
}

function FilterChip({ selected, onClick, children }) {
    return (
        <button
            onClick={onClick}
            aria-pressed={selected}
            className={`
                inline-flex items-center rounded-full px-4 py-1.5 text-sm font-medium
                transition-colors cursor-pointer border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                ${
                    selected
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-transparent text-foreground border-[rgba(185,191,198,0.4)] hover:border-[rgb(38,43,49)]'
                }
            `}
        >
            {children}
        </button>
    );
}
